import React, { FC, useEffect, useState } from 'react';
import Tag from '../../components/tags/Tag';
import Typography from '../../components/typography/Typography';

import { useSelector } from 'react-redux';
import { chartColors } from '../../components/charts/hardware/AssetAgeChart';
import { OsScore } from '../../components/charts/hardware/OsChart';
import StatsDonutChart, {
  ChartDataItem,
} from '../../components/charts/stats/StatsDonutChart';
import {
  OrderProductWorkstation,
  WorkstationAv,
} from '../../interfaces/Assets';
import { RootState } from '../../state/store';
interface hardwareGraphs {
  hardware: any;
  filter?: any;
  yearFilter?: (year: number) => void;
}

type AgeSegment = {
  ageLabel: string;
  age: number;
  year: number;
  percentage: number;
  color: string;
  count: number;
};

interface DeviceInfo {
  manufacturer?: string;
  name: string;
  architecture: string;
  lastBootTime: number;
  buildNumber: string;
  releaseId?: string;
  servicePackMajorVersion: number;
  servicePackMinorVersion: number;
  locale: string;
  language: string;
  needsReboot: boolean;
  deviceId: number;
  timestamp: number;
}

type OperatingSystem =
  | 'Windows 11'
  | 'Windows 10'
  | 'Windows 8.1'
  | 'Windows 8'
  | 'Windows 7'
  | 'Windows XP'
  | 'Ubuntu'
  | 'Windows Server 2025'
  | 'Windows Server 2022'
  | 'Windows Server 2019'
  | 'Windows Server 2016'
  | 'Windows Server 2012'
  | 'Other';

const OPERATING_SYSTEMS: OperatingSystem[] = [
  'Windows 11',
  'Windows 10',
  'Windows 8.1',
  'Windows 8',
  'Windows 7',
  'Windows XP',
  'Ubuntu',
  'Windows Server 2012',
  'Windows Server 2016',
  'Windows Server 2019',
  'Windows Server 2022',
  'Windows Server 2025',
];

type OSCountWithColor = {
  count: number;
  color: string;
};

type ResultType = 'CMC AV' | 'Microsoft Defender' | 'Not Detected' | 'Total';
type ProductEntry = {
  productName: string;
  productState?: string;
  definitionStatus?: string;
  version?: string;
  deviceId: number;
  timestamp?: number;
};

export const HardwareGraphs: FC<hardwareGraphs> = ({ hardware, filter }) => {
  if (
    !hardware ||
    !Array.isArray(hardware.assets) ||
    !hardware.antivirus ||
    !Array.isArray(hardware.antivirus.results) ||
    !hardware.os ||
    !Array.isArray(hardware.os.results)
  ) {
    return null;
  }

  const modules = useSelector((state: RootState) => state.auth.modules);
  const [workstationsAv, setWorkstationsAv] = useState([]);
  const [workstationsOldest, setWorkstationsOldest] = useState(0);
  const [managedAssets, setManagedAssets] = useState<number>(0);
  const [unmanagedAssets, setUnmanagedAssets] = useState<number>(0);
  const [totalAvDetected, setTotalAvDetected] = useState<number>(0);
  const [managedAssetChartData, setManagedAssetChartData] = useState<
    ChartDataItem[]
  >([]);
  const [avChartData, setAvChartData] = useState<ChartDataItem[]>([]);
  const [assetAgeChartData, setAssetAgeChartData] = useState<ChartDataItem[]>(
    []
  );
  const [unmanagedAssetsPercentage, setManagedAssetsPercentage] =
    useState<number>(0);
  const [segments, setSegments] = useState<AgeSegment[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [osCounts, setOsCounts] = useState<Record<string, OSCountWithColor>>(
    {}
  );
  const [oldestOs, setOldestOs] = useState(0);
  const handleRemoteManaged = (managed: number, unmanaged: number) => {
    setManagedAssetChartData([
      {
        name: 'Managed',
        percentage: managed,
      },
      {
        name: 'Unmanaged',
        percentage: unmanaged,
      },
    ]);
  };

  const handleAvData = (data: ProductEntry[]) => {
    const getAVSummary = (): Record<
      ResultType,
      {
        count: number;
        originalName: string;
      }
    > => {
      const summary: Record<
        ResultType,
        { count: number; originalName: string }
      > = {
        'CMC AV': { count: 0, originalName: '' },
        'Microsoft Defender': { count: 0, originalName: '' },
        'Not Detected': { count: 0, originalName: '' },
      };

      data.forEach(({ productName }) => {
        if (!productName) return;

        let category: ResultType | null = null;

        if (productName.includes('Bitdefender')) {
          category = 'CMC AV';
        } else if (productName.includes('Microsoft Defender')) {
          category = 'Microsoft Defender';
        } else if (productName === 'NONE') {
          category = 'Not Detected';
        }

        if (category) {
          summary[category].count++;
          if (!summary[category].originalName) {
            summary[category].originalName = productName;
          }
        }
      });

      return summary;
    };

    const avSummary = getAVSummary();
    const totalDevices = Object.keys(hardware.assets).length;

    const totalAv =
      avSummary['CMC AV'].count +
      avSummary['Microsoft Defender'].count +
      avSummary['Not Detected'].count;

    const chartData = [
      {
        name: 'CMC AV',
        percentage:
          Math.round((avSummary['CMC AV'].count / totalDevices) * 100 * 100) /
          100,
      },
      {
        name: 'Microsoft Defender',
        percentage:
          Math.round(
            (avSummary['Microsoft Defender'].count / totalDevices) * 100 * 100
          ) / 100,
      },
      {
        name: 'Not Detected',
        percentage:
          Math.round(
            (avSummary['Not Detected'].count / totalDevices) * 100 * 100
          ) / 100,
      },
    ];
    setAvChartData(chartData);
    setTotalAvDetected(totalAv);
  };

  const handleDeviceAgeChart = (data) => {
    const now = new Date();
    const ageMap: Record<string, { count: number; year: number; age: number }> =
      {};

    data.forEach((item) => {
      if (item.ordPro_invoiceDate) {
        const invoiceDate = new Date(item.ordPro_invoiceDate);
        let age = now.getFullYear() - invoiceDate.getFullYear();

        const isBeforeAnniversary =
          now.getMonth() < invoiceDate.getMonth() ||
          (now.getMonth() === invoiceDate.getMonth() &&
            now.getDate() < invoiceDate.getDate());
        if (isBeforeAnniversary) age--;

        const year = invoiceDate.getFullYear();
        const key = `Age ${age}`;
        if (!ageMap[key]) {
          ageMap[key] = { count: 0, year, age };
        }
        ageMap[key].count += 1;
      }
    });

    const total = Object.values(ageMap).reduce(
      (sum, item) => sum + item.count,
      0
    );

    const sorted = Object.entries(ageMap)
      .sort((a, b) => a[1].age - b[1].age)
      .map(([label, { count, year, age }], index) => ({
        ageLabel: label,
        age,
        year,
        percentage: (count / total) * 100,
        color: chartColors[index % chartColors.length],
        count: count,
      }));

    if (!sorted.length) return;

    setSegments(sorted);

    const chartData = sorted.map((segment) => {
      return {
        name: segment.age,
        percentage: Math.round(segment.percentage),
      };
    });
    setAssetAgeChartData(chartData);
    setWorkstationsOldest(sorted[sorted.length - 1].age);
  };

  const handleosData = (data) => {
    const score = () => {
      let lowestVersion = null;

      for (const key in osCounts) {
        if (key.startsWith('Windows')) {
          const match = key.match(/\d+/);
          if (match) {
            const version = parseInt(match[0], 10);
            if (lowestVersion === null || version < lowestVersion) {
              lowestVersion = version;
            }
          }
        }
      }
      return lowestVersion ? lowestVersion : 0;
    };
    const tempCounts: Record<string, OSCountWithColor> = {};

    data.forEach((device) => {
      const match = OPERATING_SYSTEMS.find((os) => device.name.includes(os));
      const key: string = match ?? 'Other';

      if (!tempCounts[key]) {
        const colorIndex = Object.keys(tempCounts).length % chartColors.length;
        tempCounts[key] = { count: 1, color: chartColors[colorIndex] };
      } else {
        tempCounts[key].count += 1;
      }
    });
    setOsCounts(tempCounts);

    const rechartsRow = Object.entries(tempCounts).map(([os, info]) => ({
      name: os,
      percentage: info.count,
    }));

    if (Object.keys(hardware.assets).length != Object.keys(data).length) {
      rechartsRow.push({
        name: 'Unknown',
        percentage:
          Object.keys(hardware.assets).length - Object.keys(data).length,
      });
    }

    setChartData(rechartsRow);
    setOldestOs(score());
  };

  const handleLegendClick = (e) => {
    console.log(e);
    filter(e);
  };

  const handleLegendClickYear = (years: number) => {
    if (!filter) return;

    filter(years);
  };

  useEffect(() => {
    const cleanedUpAv: WorkstationAv[] = [];
    const managed: number = hardware.assets.filter(
      (item: OrderProductWorkstation) =>
        item.device_id !== null && item.device_id !== ''
    ).length;

    hardware.antivirus.results.forEach((av: WorkstationAv) => {
      if (av.productState !== 'OFF') cleanedUpAv.push(av);
    });
    setManagedAssets(managed);
    setUnmanagedAssets(hardware.assets.length - managed);
    setManagedAssetsPercentage((managed / hardware.assets.length) * 100);
    handleRemoteManaged(managed, hardware.assets.length - managed);
    handleDeviceAgeChart(hardware.assets);
    handleosData(hardware.os.results);
    handleAvData(cleanedUpAv);
    setWorkstationsAv(cleanedUpAv);
  }, [hardware]);

  return (
    <div>
      <div className="grid lg:grid-cols-4 md:grid-cols-2 dark:border-gray-700 my-5 border rounded-lg mh-[350px]">
        <div className="border-r p-4 dark:border-gray-700">
          {unmanagedAssetsPercentage < 70 ? (
            <Tag style="bussy" label="Below treshhold" />
          ) : (
            <Tag style="success" label="Looks good" />
          )}
          <Typography tag="h3" type="subtitle">
            Managed devices
          </Typography>
          <Typography tag="span" type="label">
            Devices that are remotely managed by cmc
          </Typography>
          {managedAssetChartData && (
            <StatsDonutChart
              data={managedAssetChartData}
              unit="st"
              onLegendClick={handleLegendClick}
            />
          )}
        </div>

        <div className="border-r p-4 dark:border-gray-700">
          {workstationsOldest > 3 ? (
            <Tag style="bussy" label="Upgrade the oldest devices" />
          ) : (
            <Tag style="success" label="Looks good" />
          )}
          <Typography tag="h3" type="subtitle">
            Hardware age
          </Typography>
          <Typography tag="span" type="label">
            Based on the invoice date of the device
          </Typography>
          {assetAgeChartData ? (
            <StatsDonutChart
              data={assetAgeChartData}
              suffix="Jaar"
              onLegendClick={handleLegendClickYear}
            />
          ) : (
            ''
          )}
        </div>

        <div className="border-r p-4 dark:border-gray-700">
          {workstationsAv.some(
            (e: WorkstationAv) => e.productName === 'NONE'
          ) || totalAvDetected != Object.keys(hardware.assets).length ? (
            <Tag style="danger" label="Unprotected devices discovered" />
          ) : (
            <Tag style="success" label="Looks good" />
          )}
          <Typography tag="h3" type="subtitle">
            Antivirus
          </Typography>
          <Typography tag="span" type="label">
            This only works for CMC managed devices.
          </Typography>
          <StatsDonutChart
            data={avChartData}
            onLegendClick={handleLegendClick}
          />
        </div>

        <div className="p-4">
          <OsScore lowestOsVersion={oldestOs} />
          <Typography tag="h3" type="subtitle">
            Operating Systems
          </Typography>
          <Typography tag="span" type="label">
            This only works for CMC managed devices.
          </Typography>
          <StatsDonutChart
            data={chartData}
            unit="st"
            onLegendClick={handleLegendClick}
          />
        </div>
      </div>
    </div>
  );
};

export default HardwareGraphs;
